import * as React from 'react';
import { t } from 'i18next';

import { BaseComponent } from '../../BaseComponent';
import { LayoutType } from '../../../constants/LayoutConstants';
import { TooltipIds } from '../../../constants/TooltipIds';
import { StatsEventAction, Stats } from '../../../utils/Stats';
import { VisibilityStore } from '../../../stores/VisibilityStore';
import { ProjectSettingsStore } from '../../../stores/ProjectSettingsStore';
import { TooltipStore } from '../../../stores/TooltipStore';
import { TooltipActions } from '../../../actions/TooltipActions';
import { ViewSettingsActions } from '../../../actions/ViewSettingsActions';
import { SidebarModule, PopupMenu } from '../../../constants/Enums';
import { VisibilityActions } from '../../../actions/VisibilityActions';
import { SelectBoxOption } from '../../../stores/SelectBoxStore';
import { FloorPlanActions } from '../../../actions/FloorPlanActions';
import { GlobalHelper } from '../../../utils/GlobalHelper';
import { ProjectSettingsActions } from '../../../actions/ProjectSettingsActions';
import { PopupFlagPosition } from '../../../constants/PopupFlagPositions';
import { PersistentStore } from '../../../stores/PersistentStore';
import { CoreTooltip } from '../../CoreTooltip';
import { BasePopupMenu } from '../../popupmenu/BasePopupMenu';

interface FloorPlanPopupProps extends React.Props<FloorPlanPopup> {
    layoutType: LayoutType;
}

interface FloorPlanPopupState {
    open: boolean;
    floorPlanVisibility: boolean;
    activeTooltip: TooltipIds | null;
}

export class FloorPlanPopup extends BaseComponent<FloorPlanPopupProps, FloorPlanPopupState> {
    protected static sendEvent(action: StatsEventAction, label?: string): void {
        Stats.sendEvent({
            category: 'FloorPlan',
            action,
            label
        });
    }

    public componentWillMount(): void {
        this.addStoreListener(VisibilityStore, this.onChange);
        this.addStoreListener(ProjectSettingsStore, this.onChange);
        this.addStoreListener(TooltipStore, this.onChange);
        this.onChange();
    }

    public render(): JSX.Element {
        return (
            <BasePopupMenu
                defaultValue={null}
                flagPosition={PopupFlagPosition.TOPLEFT}
                open={this.state.open}
                toggleCallback={this.toggleSelect}
                className="toolbar-button toolbar-floorplan-button toolbar-button-popup"
                options={this.options()}
                title={{
                    label: t('Floor Plan'),
                    className: 'egr-select-option-header-info',
                    link: PersistentStore.getState().language === 'de' ? 'https://youtu.be/MLQs8qlZWwA' : 'https://youtu.be/srIMa1BMAK8'
                }}
            >
                {this.state.open ? null : (
                    <CoreTooltip
                        config={{
                            label: t('Floor Plan'),
                            forceLabel: (this.state.activeTooltip === TooltipIds.FLOORPLAN_ICON ? t('Insert a floor plan.') : null)
                        }}
                    />
                )}
            </BasePopupMenu>
        );
    }

    protected toggleSelect = (pValue: boolean) => {
        if (!pValue) {
            TooltipActions.hideTooltip(TooltipIds.FLOORPLAN_ICON);
            ViewSettingsActions.changeSidebar(SidebarModule.NONE);
            VisibilityActions.setPopupMenu(PopupMenu.FLOORPLAN);
            Stats.sendEvent({
                category: 'Interaction',
                action: 'floorplan'
            });
        } else {
            VisibilityActions.hidePopupMenu(PopupMenu.FLOORPLAN);
        }
    };

    protected onChange = (): void => {
        this.setState({
            open: VisibilityStore.getState().popupMenu === PopupMenu.FLOORPLAN,
            floorPlanVisibility: ProjectSettingsStore.getFloorPlanVisibility(),
            activeTooltip: TooltipStore.getState().activeId
        });
    };

    protected options(): Array<SelectBoxOption> {
        return [
            {
                value: 'insert-fp',
                name: t('Insert'),
                className: 'insert-button',
                callback: () => {
                    VisibilityActions.hidePopupMenu(PopupMenu.FLOORPLAN);
                    FloorPlanActions.getImageFile();
                }
            },
            {
                value: 'toggle-fp',
                name: this.state.floorPlanVisibility ? t('Hide') : t('Unhide'),
                className: this.state.floorPlanVisibility ? ' hide-button' : ' show-button',
                disabled: (GlobalHelper.floorPlan == null || !GlobalHelper.floorPlan.hasTexture()),
                callback: () => {
                    if (ProjectSettingsStore.getFloorPlanVisibility()) {
                        ProjectSettingsActions.hideFloorPlan();
                        FloorPlanPopup.sendEvent('visibilitychange', 'hidden');
                    } else {
                        ProjectSettingsActions.showFloorPlan();
                        FloorPlanPopup.sendEvent('visibilitychange', 'visible');
                    }
                }
            },
            {
                value: 'setup-fp',
                name: t('Setup'),
                className: 'setup-button',
                disabled: GlobalHelper.floorPlan == null || !GlobalHelper.floorPlan.hasTexture() || !this.state.floorPlanVisibility,
                callback: () => {
                    VisibilityActions.setPopupMenu(PopupMenu.FLOORPLAN_SETUP);
                    FloorPlanPopup.sendEvent('setup');
                }
            }
        ];
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/overlay/floorplan/FloorPlanPopup.tsx